import { XIcon } from "@heroicons/react/outline";
import { ConfirmModal } from "components/confirmModal";
import { DropdownMenu } from "components/dropdownMenu";
import { SeatItem, SeatLayoutPosition, SeatLayoutStatus } from "interfaces";
import { isEmpty } from "lodash";
import { SetStateAction, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { twMerge } from "tailwind-merge";
import { SEAT_STATUS, WALKWAY_VALUE } from "utills/constants";

const INITIAL_MODAL_CONFIG = {
  open: false,
  id: "",
  title: "",
  content: "",
  submit: () => {},
};

interface WalkwayElementPropType {
  position: "left" | "right";
  isEdit: boolean;
  cell: SeatItem;
  cellStyle: any;
  handleRemoveWalkway: (seatId: string, position: "left" | "right") => void;
  setModalConfig: (value: SetStateAction<{
      open: boolean;
      id: string;
      title: string;
      content: string;
      submit: () => void;
  }>) => void;
}

export const SeatmapTable = ({isEdit, tableData, rowNameType, rowNamePosition, _updateSeatStatus, handleRemoveWalkway}: {isEdit: boolean; rowNameType: string; tableData: any; rowNamePosition: string, _updateSeatStatus: (status: SeatLayoutStatus, id: string) => void, handleRemoveWalkway: (seatId: string, position: "left" | "right") => void }) => {
  const [modalConfig, setModalConfig] = useState(INITIAL_MODAL_CONFIG);

  const _onCloseActivePhaseModal = () => {
    setModalConfig({
      ...INITIAL_MODAL_CONFIG,
      title: modalConfig?.title ?? "",
      content: modalConfig?.content ?? "",
    });
  }
  
  const getAlphabeticLabel = (index: number) => {
    const base = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (index < 26) {
      return base[index];
    } else {
      const firstChar = base[Math.floor(index / 26) - 1];
      const secondChar = base[index % 26];
      return firstChar + secondChar;
    }
  };

  const getTablePotiion = (position: string) => {
    if (position === SeatLayoutPosition.LEFT_TOP_DOWN) {
      return {
        tbody: "flex flex-col items-start justify-center gap-3",
        tr: "flex items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.LEFT_BOTTOM_UP) {
      return {
        tbody: "flex flex-col-reverse items-start justify-center gap-3",
        tr: "flex items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.RIGHT_TOP_DOWN) {
      return {
        tbody: "flex flex-col items-end justify-center gap-3",
        tr: "flex flex-row-reverse items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.RIGHT_BOTTOM_UP) {
      return {
        tbody: "flex flex-col-reverse items-end justify-center gap-3",
        tr: "flex flex-row-reverse items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.TOP_LEFT) {
      return {
        tbody: "flex flex-row items-start justify-center gap-3",
        tr: "flex flex-col items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.BOTTOM_LEFT) {
      return {
        tbody: "flex flex-row items-end justify-center gap-3",
        tr: "flex flex-col-reverse items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.TOP_RIGHT) {
      return {
        tbody: "flex flex-row-reverse items-start justify-center gap-3",
        tr: "flex flex-col items-center justify-center gap-3"
      }
    }

    if (position === SeatLayoutPosition.BOTTOM_RIGHT) {
      return {
        tbody: "flex flex-row-reverse items-end justify-center gap-3",
        tr: "flex flex-col-reverse items-center justify-center gap-3"
      }
    }

    return {
      tbody: "flex flex-col items-start justify-center gap-3",
      tr: "flex items-center justify-center gap-3"
    }
  }

  const getTableElementStype = (status: string) => {
    if (status === SeatLayoutStatus.HIDE) {
      return "border-[#CCD3FF] text-[#4460FD] bg-[#CCD3FF]";
    }

    if (status === SeatLayoutStatus.IN_CART) {
      return "border-[#F7A06E] text-[#FFF] bg-[#F7A06E]";
    }

    return "";
  }

  const getValidWalkwayPosition = (
    walkway: string[],
    rowNamePosition: string,
    position: string
  ) => {    
    if (
      (rowNamePosition === SeatLayoutPosition.LEFT_TOP_DOWN ||
        rowNamePosition === SeatLayoutPosition.LEFT_BOTTOM_UP ||
        rowNamePosition === SeatLayoutPosition.RIGHT_TOP_DOWN ||
        rowNamePosition === SeatLayoutPosition.RIGHT_BOTTOM_UP) &&
      (position?.includes("left") || position?.includes("right"))
    ) {
      if (walkway?.length !== 0 && walkway?.includes(WALKWAY_VALUE.IN_LEFT) && position === SeatLayoutStatus.WALKWAY_ON_THE_LEFT) {
        return false;
      }

      if (walkway?.length !== 0 && walkway?.includes(WALKWAY_VALUE.IN_RIGHT) && position === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT) {
        return false;
      }

      return true;
    }

    if (
      (rowNamePosition === SeatLayoutPosition.TOP_LEFT ||
        rowNamePosition === SeatLayoutPosition.TOP_RIGHT ||
        rowNamePosition === SeatLayoutPosition.BOTTOM_LEFT ||
        rowNamePosition === SeatLayoutPosition.BOTTOM_RIGHT) &&
      (position?.includes("above") || position?.includes("below"))
    ) {
      if (walkway?.length !== 0 && walkway?.includes(WALKWAY_VALUE.IN_LEFT) && position === SeatLayoutStatus.WALKWAY_ABOVE) {
        return false;
      }

      if (walkway?.length !== 0 && walkway?.includes(WALKWAY_VALUE.IN_RIGHT) && position === SeatLayoutStatus.WALKWAY_BELOW) {
        return false;
      }

      return true;
    }

    return false;
  };
  
  const processTableData = () => {
    if (!tableData.length) return { rows: 0, columns: 0, organizedData: {} };
    
    let maxRowIndex = 0;
    let maxColIndex = 0;
    const organizedData: any = {};
    
    // Find the maximum row and column indices and organize data
    tableData.forEach((cell: any) => {
      maxRowIndex = Math.max(maxRowIndex, (cell?.row_index || 0));
      maxColIndex = Math.max(maxColIndex, (cell.column_index || 0));
      
      // Create column if it doesn't exist
      if (!organizedData[cell.row_index]) {
        organizedData[cell.row_index] = {};
      }
      
      organizedData[cell.row_index][cell.column_index] = cell;
    });
    
    return {
      rows: maxRowIndex + 1,
      columns: maxColIndex + 1,
      organizedData
    };
  };

  const handleConfirmAction = (
    cell: SeatItem,
    type: {
      value: SeatLayoutStatus;
      label: string;
      readOnly: boolean;
    }
  ) => {
    if (isEdit && cell?.id && type.value === SeatLayoutStatus.AVAILABLE) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure to available this seat?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.AVAILABLE, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.UNAVAILABLE) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure to unavailable this seat?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.UNAVAILABLE, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.INACTIVE) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure to inactive this seat?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.INACTIVE, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.HIDE) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure to hide this seat?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.HIDE, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.WALKWAY_ON_THE_LEFT) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure you want to add a walkway to the left of the seat?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.WALKWAY_ON_THE_LEFT, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure you want to add a walkway to the right of the seat?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.WALKWAY_ON_THE_RIGHT, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.WALKWAY_ABOVE) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure you want to add a top walkway?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.WALKWAY_ABOVE, cell?.id ?? "");
        },
      });

      return;
    }

    if (isEdit && cell?.id && type.value === SeatLayoutStatus.WALKWAY_BELOW) {
      setModalConfig({
        open: true,
        id: cell?.id,
        title: "Confirm this action",
        content: "Are you sure you want to add a bottom walkway?",
        submit: () => {
          _updateSeatStatus(SeatLayoutStatus.WALKWAY_BELOW, cell?.id ?? "");
        },
      });

      return;
    }
  };

  const RenderWalkwayElement = (props: WalkwayElementPropType) => {
    const { position, isEdit, cell, cellStyle, handleRemoveWalkway, setModalConfig } = props;

    return (
      <td
        style={{
          ...cellStyle,
        }}
        className="p-1.5"
        title={cell ? `Status: ${cell?.status}` : 'No data'}
      >
        <div className="relative flex items-center justify-center size-9 border border-solid font-medium rounded-full select-none
                          border-[#868E95] bg-[#868E95]"
        >
          <DropdownMenu
            position="bottom-start"
            align="left"
            label={<span className="opacity-0 px-[12px] py-[8px] rounded-full">w</span>}
            items={[
              {
                label: "Remove",
                onClick: () => {
                  if (isEdit && cell?.id) {
                    setModalConfig({
                      open: true,
                      id: cell?.id,
                      title: "Confirm this action",
                      content: "Are you sure remove this walkway?",
                      submit: () => {
                        handleRemoveWalkway(cell?.id ?? "", position);
                      }
                    });

                    return;
                  }

                  handleRemoveWalkway(cell?.id ?? "", position);
                }
              }
            ]}
          />
        </div>
      </td>
    )
  }

  const renderTable = () => {
    const { rows, columns, organizedData } = processTableData();
    
    const cellStyle: any = {
      width: `40px`,
      height: `40px`,
      textAlign: 'center',
      verticalAlign: 'middle',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    };
    
    return (
      <table className="w-max">
        <tbody className={`${getTablePotiion(rowNamePosition).tbody}`}>
          {Array.from({ length: rows }, (_, rowIndex) => {
            let seatNumber = 0; // counter cho từng hàng

            return (
              <tr key={`row-${rowIndex}`} className={`${getTablePotiion(rowNamePosition).tr}`}>
                <th 
                  key={`header-${rowIndex}`}
                  style={{
                    ...cellStyle,
                    cursor: 'default'
                  }}
                >
                  <div className="flex items-center justify-center h-full text-base">
                    {rowNameType === "alphabet" ? getAlphabeticLabel(rowIndex) : rowIndex + 1}
                  </div>
                </th>

                {Array.from({ length: columns }, (_, columnIndex) => {
                  const cell: SeatItem = organizedData[rowIndex]?.[columnIndex];

                  if (!cell?.id) {
                    return null;
                  }

                  // Ghế thường => tăng seatNumber
                  seatNumber++;

                  // Status: Hide, In Cart
                  if (cell?.status === SeatLayoutStatus.HIDE || cell?.status === SeatLayoutStatus.IN_CART) {
                    return (
                      <div key={crypto.randomUUID()} className={`${getTablePotiion(rowNamePosition).tr}`}>
                        {cell?.walkway?.map((item) => {
                          if (item?.includes("left") &&
                          (rowNamePosition === SeatLayoutPosition.LEFT_TOP_DOWN ||
                            rowNamePosition === SeatLayoutPosition.LEFT_BOTTOM_UP ||
                            rowNamePosition === SeatLayoutPosition.TOP_LEFT ||
                            rowNamePosition === SeatLayoutPosition.TOP_RIGHT
                          )) {
                            return (
                              <RenderWalkwayElement
                                key={crypto.randomUUID()}
                                position="left"
                                isEdit={isEdit}
                                cell={cell}
                                cellStyle={cellStyle}
                                handleRemoveWalkway={handleRemoveWalkway}
                                setModalConfig={setModalConfig}
                              />
                            )
                          }
                        })}

                        {cell?.walkway?.map((item) => {
                          if (item?.includes("right") &&
                          (rowNamePosition === SeatLayoutPosition.RIGHT_TOP_DOWN ||
                            rowNamePosition === SeatLayoutPosition.RIGHT_BOTTOM_UP ||
                            rowNamePosition === SeatLayoutPosition.BOTTOM_LEFT ||
                            rowNamePosition === SeatLayoutPosition.BOTTOM_RIGHT
                          )) {
                            return (
                              <RenderWalkwayElement
                                key={crypto.randomUUID()}
                                position="right"
                                isEdit={isEdit}
                                cell={cell}
                                cellStyle={cellStyle}
                                handleRemoveWalkway={handleRemoveWalkway}
                                setModalConfig={setModalConfig}
                              />
                            )
                          }
                        })}

                        <td
                          key={`cell-${rowIndex}-${columnIndex}`}
                          style={{
                            ...cellStyle,
                          }}
                          className="p-1.5"
                          title={cell ? `Status: ${cell?.status}` : 'No data'}
                        >
                          <div className={`relative flex items-center justify-center size-9 border border-solid font-medium rounded-full select-none
                                            ${getTableElementStype(cell?.status)}
                                          `}
                          >
                            <DropdownMenu
                              position="bottom-start"
                              align="left"
                              label={
                                <div className="relative flex items-center justify-center size-9 border border-solid font-medium rounded-full select-none">
                                  {seatNumber}
                                </div>
                              }
                              items={(SEAT_STATUS).filter(type => {
                                if (type.value !== SeatLayoutStatus.WALKWAY && type.value.includes("walkway")) {
                                  const isValid = getValidWalkwayPosition(cell?.walkway, rowNamePosition, type?.value);

                                  if (isValid) return type;
                                  else return null;
                                }

                                // Nếu seat có status là in_cart thì chỉ có walkway action
                                if ((cell?.status === SeatLayoutStatus.IN_CART) && !type.value.includes("walkway")) {
                                  return null;
                                }

                                if (type.value !== cell?.status && type.readOnly !== true && type.value !== SeatLayoutStatus.WALKWAY) return type;
                              }).map(type => ({
                                label: type.label,
                                onClick: () => {
                                  if (isEdit) {
                                    handleConfirmAction(cell, type);
                                    return;
                                  }

                                  _updateSeatStatus(type.value, (cell?.id ?? ""));
                                }
                              }))}
                            />
                          </div>
                        </td>

                        {cell?.walkway?.map((item) => {
                          if (item?.includes("left") &&
                          (rowNamePosition === SeatLayoutPosition.RIGHT_TOP_DOWN ||
                            rowNamePosition === SeatLayoutPosition.RIGHT_BOTTOM_UP ||
                            rowNamePosition === SeatLayoutPosition.BOTTOM_LEFT ||
                            rowNamePosition === SeatLayoutPosition.BOTTOM_RIGHT
                          )) {
                            return (
                              <RenderWalkwayElement
                                key={crypto.randomUUID()}
                                position="left"
                                isEdit={isEdit}
                                cell={cell}
                                cellStyle={cellStyle}
                                handleRemoveWalkway={handleRemoveWalkway}
                                setModalConfig={setModalConfig}
                              />
                            )
                          }
                        })}

                        {cell?.walkway?.map((item) => {
                          if (item?.includes("right") &&
                          (rowNamePosition === SeatLayoutPosition.LEFT_TOP_DOWN ||
                            rowNamePosition === SeatLayoutPosition.LEFT_BOTTOM_UP ||
                            rowNamePosition === SeatLayoutPosition.TOP_LEFT ||
                            rowNamePosition === SeatLayoutPosition.TOP_RIGHT
                          )) {
                            return (
                              <RenderWalkwayElement
                                key={crypto.randomUUID()}
                                position="right"
                                isEdit={isEdit}
                                cell={cell}
                                cellStyle={cellStyle}
                                handleRemoveWalkway={handleRemoveWalkway}
                                setModalConfig={setModalConfig}
                              />
                            )
                          }
                        })}
                      </div>                  
                    )
                  }
                  
                  return (
                    <div key={crypto.randomUUID()} className={`${getTablePotiion(rowNamePosition).tr}`}>
                      {cell?.walkway?.map((item) => {
                        if (item?.includes("left") &&
                        (rowNamePosition === SeatLayoutPosition.LEFT_TOP_DOWN ||
                          rowNamePosition === SeatLayoutPosition.LEFT_BOTTOM_UP ||
                          rowNamePosition === SeatLayoutPosition.TOP_LEFT ||
                          rowNamePosition === SeatLayoutPosition.TOP_RIGHT
                        )) {
                          return (
                            <RenderWalkwayElement
                              key={crypto.randomUUID()}
                              position="left"
                              isEdit={isEdit}                            
                              cell={cell}
                              cellStyle={cellStyle}
                              handleRemoveWalkway={handleRemoveWalkway}
                              setModalConfig={setModalConfig}
                            />
                          )
                        }
                      })}

                      {cell?.walkway?.map((item) => {
                        if (item?.includes("right") &&
                        (rowNamePosition === SeatLayoutPosition.RIGHT_TOP_DOWN ||
                          rowNamePosition === SeatLayoutPosition.RIGHT_BOTTOM_UP ||
                          rowNamePosition === SeatLayoutPosition.BOTTOM_LEFT ||
                          rowNamePosition === SeatLayoutPosition.BOTTOM_RIGHT
                        )) {
                          return (
                            <RenderWalkwayElement
                              key={crypto.randomUUID()}
                              position="right"
                              isEdit={isEdit}
                              cell={cell}
                              cellStyle={cellStyle}
                              handleRemoveWalkway={handleRemoveWalkway}
                              setModalConfig={setModalConfig}
                            />
                          )
                        }
                      })}

                      <td 
                        key={`cell-${rowIndex}-${columnIndex}`}
                        style={{
                          ...cellStyle,
                        }}
                        className="p-1.5"
                        title={cell ? `Status: ${cell?.status}` : 'No data'}
                      >
                        {cell?.status === SeatLayoutStatus.SOLD_OUT ? (
                          <DropdownMenu
                              position="bottom-start"
                              align="left"
                              label={
                              <div className={twMerge("relative flex items-center justify-center size-9 border border-solid font-medium rounded-full select-none", 
                                "border-brand-500 text-gray-300 bg-brand-500")}>
                                {seatNumber}
                              </div>
                              }
                              items={(SEAT_STATUS).filter(type => {
                                if (type.value !== SeatLayoutStatus.WALKWAY && type.value.includes("walkway")) {
                                  const isValid = getValidWalkwayPosition(cell?.walkway, rowNamePosition, type?.value);

                                  if (isValid) return type;
                                  else return null;
                                }

                                // Nếu seat có status là sold_out thì chỉ có walkway action
                                if ((cell?.status === SeatLayoutStatus.SOLD_OUT) && !type.value.includes("walkway")) {
                                  return null;
                                }

                                if (type.value !== cell?.status && type.readOnly !== true && type.value !== SeatLayoutStatus.WALKWAY) return type;
                              }).map(type => ({
                                label: type.label,
                                onClick: () => {
                                  if (isEdit) {
                                    handleConfirmAction(cell, type);
                                    return;
                                  }

                                  _updateSeatStatus(type.value, (cell?.id ?? ""));
                                }
                              }))}
                            />
                        ) : (
                          <DropdownMenu
                            position="bottom-start"
                            align="left"
                            label={
                              <div className={twMerge("relative flex items-center justify-center size-9 border border-solid font-medium rounded-full select-none", 
                                cell?.status === SeatLayoutStatus.INACTIVE 
                                ? "border-gray-300 text-black bg-gray-300" 
                                : "border-brand-500 text-brand-500 bg-white")}>
                                {cell?.status === SeatLayoutStatus.UNAVAILABLE ? <XIcon className="size-7 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" /> : seatNumber}
                              </div>
                            }
                            items={(SEAT_STATUS).filter(type => {
                              if (type.value !== SeatLayoutStatus.WALKWAY && type.value.includes("walkway")) {
                                const isValid = getValidWalkwayPosition(cell?.walkway, rowNamePosition, type?.value);

                                if (isValid) return type;
                                else return null;
                              }

                              if (type.value !== cell?.status && type.readOnly !== true && type.value !== SeatLayoutStatus.WALKWAY) return type;
                            }).map(type => ({
                              label: type.label,
                              onClick: () => {
                                if (isEdit) {
                                  handleConfirmAction(cell, type);
                                  return;
                                }

                                _updateSeatStatus(type.value, (cell?.id ?? ""));
                              }
                            }))}
                          />
                        )}
                      </td>

                      {cell?.walkway?.map((item) => {
                        if (item?.includes("left") &&
                        (rowNamePosition === SeatLayoutPosition.RIGHT_TOP_DOWN ||
                          rowNamePosition === SeatLayoutPosition.RIGHT_BOTTOM_UP ||
                          rowNamePosition === SeatLayoutPosition.BOTTOM_LEFT ||
                          rowNamePosition === SeatLayoutPosition.BOTTOM_RIGHT
                        )) {
                          return (
                            <RenderWalkwayElement
                              key={crypto.randomUUID()}
                              position="left"
                              isEdit={isEdit}
                              cell={cell}
                              cellStyle={cellStyle}
                              handleRemoveWalkway={handleRemoveWalkway}
                              setModalConfig={setModalConfig}
                            />
                          )
                        }
                      })}

                      {cell?.walkway?.map((item) => {
                        if (item?.includes("right") &&
                        (rowNamePosition === SeatLayoutPosition.LEFT_TOP_DOWN ||
                          rowNamePosition === SeatLayoutPosition.LEFT_BOTTOM_UP ||
                          rowNamePosition === SeatLayoutPosition.TOP_LEFT ||
                          rowNamePosition === SeatLayoutPosition.TOP_RIGHT
                        )) {
                          return (
                            <RenderWalkwayElement
                              key={crypto.randomUUID()}
                              position="right"
                              isEdit={isEdit}
                              cell={cell}
                              cellStyle={cellStyle}
                              handleRemoveWalkway={handleRemoveWalkway}
                              setModalConfig={setModalConfig}
                            />
                          )
                        }
                      })}
                    </div>
                  );
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="max-w-full">
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={1}
        limitToBounds={true}
        disablePadding={true}
        centerOnInit={true}
        // onTransformed={handleTransformChange}
        wheel={{ step: 0.1 }} // Smoother zoom
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="flex gap-2 mb-4">
              <button 
                onClick={() => zoomIn(0.1)} 
                className="px-3 py-1 bg-gray-200 rounded"
              >
                Zoom In
              </button>
              <button 
                onClick={() => zoomOut(0.1)} 
                className="px-3 py-1 bg-gray-200 rounded"
              >
                Zoom Out
              </button>
              <button 
                onClick={() => resetTransform()} 
                className="px-3 py-1 bg-gray-200 rounded"
              >
                Reset
              </button>
            </div>
            <div className="overflow-x-auto pb-4">
              <TransformComponent wrapperClass={`!min-w-full ${isEmpty(tableData) ? "h-auto" : "!h-[360px]"}`} contentClass="!w-full">
                {renderTable()}
              </TransformComponent>
            </div>
          </>
        )}
      </TransformWrapper>
      {isEmpty(tableData) ? <p className="text-center">No seat layout</p> :<></>}

      <ConfirmModal
        key="confirm-hide"
        title={modalConfig.title}
        content={modalConfig.content}
        open={modalConfig.open}
        onConfirm={modalConfig.submit}
        onClose={_onCloseActivePhaseModal}
      />
    </div>
  );
}
